import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import DeveloperPaymentForm from '../components/Forms/DeveloperPaymentForm';
import { Plus, Code, Users, Wallet, TrendingDown, Zap, ShieldCheck, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const DeveloperReceivables = () => {
    const { currency } = useAuth();
    const [payments, setPayments] = useState<any[]>([]);
    const [developers, setDevelopers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [paymentsRes, developersRes] = await Promise.all([
                api.get('/developer-payments'),
                api.get('/developers?limit=100')
            ]);
            setPayments(paymentsRes.data);
            setDevelopers(developersRes.data.developers);
        } catch (err) {
            console.error('Error fetching developer data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const stats = useMemo(() => {
        const total = payments.reduce((s, p) => s + (p.amount || 0), 0);

        // Per-developer breakdown
        const devMap: Record<string, { name: string; amount: number; count: number }> = {};
        payments.forEach(p => {
            const name = p.developer?.name || 'Unknown';
            const id = p.developerId || name;
            if (!devMap[id]) devMap[id] = { name, amount: 0, count: 0 };
            devMap[id].amount += p.amount || 0;
            devMap[id].count++;
        });
        const devBreakdown = Object.values(devMap).sort((a, b) => b.amount - a.amount);

        // This month
        const now = new Date();
        const thisMonthPayments = payments.filter(p => {
            const d = new Date(p.paymentDate);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
        const thisMonth = thisMonthPayments.reduce((s, p) => s + (p.amount || 0), 0);

        // Last month
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthPayments = payments.filter(p => {
            const d = new Date(p.paymentDate);
            return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
        });
        const lastMonth = lastMonthPayments.reduce((s, p) => s + (p.amount || 0), 0);
        const growth = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : 0;

        const topDev = devBreakdown[0];
        const topPct = total > 0 && topDev ? Math.round((topDev.amount / total) * 100) : 0;

        return { total, thisMonth, lastMonth, growth, devBreakdown, topDev, topPct, count: payments.length, devCount: devBreakdown.length };
    }, [payments]);

    const bannerColor = stats.thisMonth > stats.lastMonth && stats.lastMonth > 0
        ? 'from-rose-500 to-orange-500'
        : stats.thisMonth > 0
            ? 'from-violet-600 to-indigo-600'
            : 'from-emerald-500 to-teal-500';

    const BannerIcon = stats.thisMonth > stats.lastMonth && stats.lastMonth > 0 ? Flame : stats.thisMonth > 0 ? Zap : ShieldCheck;

    const bannerMsg = stats.count === 0
        ? 'No payments logged yet'
        : stats.thisMonth > stats.lastMonth && stats.lastMonth > 0
            ? `Costs up ${Math.abs(stats.growth).toFixed(0)}% vs last month — review spend`
            : stats.thisMonth < stats.lastMonth && stats.lastMonth > 0
                ? `Costs down ${Math.abs(stats.growth).toFixed(0)}% vs last month — well managed!`
                : `${stats.count} payments across ${stats.devCount} developer${stats.devCount !== 1 ? 's' : ''}`;

    const handleCreate = () => { setEditingPayment(null); setShowForm(true); };
    const handleEdit = (payment: any) => { setEditingPayment(payment); setShowForm(true); };
    const handleDelete = async (payment: any) => {
        if (window.confirm('Are you sure you want to delete this developer payment?')) {
            try {
                await api.delete(`/developer-payments/${payment.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting developer payment', err);
            }
        }
    };

    const columns = [
        { header: 'Date', accessor: (item: any) => new Date(item.paymentDate).toLocaleDateString() },
        { header: 'Payment #', accessor: 'paymentNumber' },
        { header: 'Developer Name', accessor: (item: any) => item.developer?.name || 'Unknown' },
        { header: 'Payment Mode', accessor: 'paymentMode' },
        { header: 'Amount', accessor: (item: any) => formatCurrency(item.amount, currency) },
    ];

    const devColors = ['bg-violet-500', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-sky-500'];

    return (
        <div className="space-y-5 p-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Code className="text-blue-600" size={26} />
                        Developers Receivables
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage and track outgoing payments to subcontractors</p>
                </div>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-sm"
                >
                    <Plus size={18} />
                    Log Payment
                </button>
            </div>

            {/* Motivational Banner */}
            <div className={`relative bg-gradient-to-r ${bannerColor} rounded-xl p-5 text-white overflow-hidden`}>
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="relative flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BannerIcon size={16} className="text-white/90" />
                            <p className="text-white/90 text-sm font-medium">{bannerMsg}</p>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(stats.total, currency)} <span className="text-base font-normal text-white/80">total paid out</span></p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-white/80 flex-wrap">
                            <span>{stats.devCount} developer{stats.devCount !== 1 ? 's' : ''}</span>
                            {stats.thisMonth > 0 && <span className="font-medium text-white">{formatCurrency(stats.thisMonth, currency)} this month</span>}
                            {stats.topDev && <span>Top: <span className="font-semibold text-white">{stats.topDev.name}</span> ({stats.topPct}%)</span>}
                        </div>
                    </div>
                    {/* This month ring */}
                    <div className="flex flex-col items-center">
                        <div className="relative w-16 h-16">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60">
                                <circle cx="30" cy="30" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6" />
                                <circle cx="30" cy="30" r="24" fill="none" stroke="white" strokeWidth="6"
                                    strokeDasharray={`${2 * Math.PI * 24}`}
                                    strokeDashoffset={`${2 * Math.PI * 24 * (1 - (stats.total > 0 ? Math.min(stats.thisMonth / stats.total, 1) : 0))}`}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-xs text-center leading-tight">
                                {stats.total > 0 ? Math.round((stats.thisMonth / stats.total) * 100) : 0}%
                            </div>
                        </div>
                        <p className="text-xs text-white/70 mt-1">This Month</p>
                    </div>
                </div>
                <div className="relative mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-700"
                        style={{ width: `${stats.total > 0 ? Math.min((stats.thisMonth / stats.total) * 100, 100) : 0}%` }} />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                            <TrendingDown size={16} className="text-violet-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Total Paid Out</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">{formatCurrency(stats.total, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.count} payments</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Users size={16} className="text-blue-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Developers</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800">{stats.devCount}</p>
                    <p className="text-xs text-gray-400 mt-0.5">subcontractors paid</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                            <Wallet size={16} className="text-indigo-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">This Month</span>
                    </div>
                    <p className="text-xl font-bold text-indigo-700">{formatCurrency(stats.thisMonth, currency)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.lastMonth > 0 ? `${stats.growth >= 0 ? '▲' : '▼'} ${Math.abs(stats.growth).toFixed(0)}% vs last month` : 'No data last month'}</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <Code size={16} className="text-emerald-600" />
                        </div>
                        <span className="text-xs font-medium text-gray-500">Top Developer</span>
                    </div>
                    <p className="text-xl font-bold text-gray-800 truncate">{stats.topDev?.name || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stats.topDev ? `${formatCurrency(stats.topDev.amount, currency)} · ${stats.topPct}%` : 'No payments yet'}</p>
                </div>
            </div>

            {/* Developer breakdown + table */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {stats.devBreakdown.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm md:col-span-1">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">By Developer</h3>
                        <div className="space-y-3">
                            {stats.devBreakdown.map((dev, i) => {
                                const pct = stats.total > 0 ? Math.round((dev.amount / stats.total) * 100) : 0;
                                return (
                                    <div key={dev.name}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-gray-600 truncate">{dev.name}</span>
                                            <span className="text-xs text-gray-500 ml-1 shrink-0">{pct}%</span>
                                        </div>
                                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-700 ${devColors[i % devColors.length]}`}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{formatCurrency(dev.amount, currency)} · {dev.count} payment{dev.count !== 1 ? 's' : ''}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className={stats.devBreakdown.length > 0 ? 'md:col-span-3' : 'md:col-span-4'}>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1">
                        <DataTable
                            data={payments}
                            columns={columns}
                            total={payments.length}
                            page={1}
                            limit={100}
                            onPageChange={() => { }}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>

            {showForm && (
                <DeveloperPaymentForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => { setShowForm(false); fetchData(); }}
                    payment={editingPayment}
                    developers={developers}
                />
            )}
        </div>
    );
};

export default DeveloperReceivables;
